import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { createGrid } from '../../services/plans/createGrid';
import { Insurance } from '../../types/plans/types';
import {
  fetchAvailableInsurancePlans,
} from '../../utils/insuranceCopyUtils';
import { AgeRangeSelect } from '../age-range-select';
import { AgeRange, coerceRangeForBackend } from '../../utils/ageRanges';
import { stateOptions } from '../../utils/stateOptions';
import { getStatusConfig } from '../../utils/getStatusConfig';

interface AddInsurancePlansModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerId: string;
  practiceIds: string[];
  defaultState?: string; // Pre-filled state from provider's practices
  existingInsuranceIds?: Set<string>; // Insurance IDs already assigned to provider
  onSuccess?: () => void; // Callback after successful addition
}

// Combined status options matching ProviderInsuranceTable
const COMBINED_STATUS_OPTIONS = [
  'Schedule Allowed',
  'Pending - Do Not Schedule',
  'Not Participating',
  'Established Patients Only',
  'PCP Match Required',
  'No Status',
];

// Helper function to map combined status to credential status and value
const getCombinedStatusMapping = (combinedStatus: string) => {
  switch (combinedStatus) {
    case 'Schedule Allowed':
      return {
        credentialStatus: 'Credentialed',
        value: 'Schedule Allowed',
      };
    case 'Pending - Do Not Schedule':
      return { credentialStatus: 'Pending', value: 'No Status' };
    case 'Not Participating':
      return {
        credentialStatus: 'Credentialed',
        value: 'Not Participating',
      };
    case 'Established Patients Only':
      return {
        credentialStatus: 'Credentialed',
        value: 'Established Patients',
      };
    case 'PCP Match Required':
      return { credentialStatus: 'Credentialed', value: 'PCP Match' };
    case 'No Status':
      return { credentialStatus: 'Credentialed', value: 'No Status' };
    default:
      return { credentialStatus: 'Credentialed', value: 'No Status' };
  }
};

// Helper function to get status config for dropdown options
const getStatusConfigForOption = (combinedStatus: string) => {
  const { credentialStatus, value } =
    getCombinedStatusMapping(combinedStatus);
  return getStatusConfig(
    value,
    undefined,
    undefined,
    credentialStatus
  );
};

const AddInsurancePlansModal: React.FC<AddInsurancePlansModalProps> = ({
  isOpen,
  onClose,
  providerId,
  practiceIds,
  defaultState,
  existingInsuranceIds = new Set(),
  onSuccess,
}) => {
  const toast = useToast();
  const [selectedState, setSelectedState] = useState<string>(defaultState || '');
  const [availablePlans, setAvailablePlans] = useState<Insurance[]>([]);
  const [selectedPlanIds, setSelectedPlanIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default values - using combined status
  const [defaultCombinedStatus, setDefaultCombinedStatus] = useState('No Status');
  const [defaultAgeRange, setDefaultAgeRange] = useState<AgeRange>({ min: null, max: null });

  // Get available states - use stateOptions which provides abbreviations
  const availableStates = useMemo(() => {
    return stateOptions.map((option) => ({
      label: option.label,
      value: option.value, // This is the abbreviation (e.g., "AZ")
    }));
  }, []);

  // Fetch available insurance plans when state changes
  useEffect(() => {
    if (selectedState && isOpen) {
      setIsLoadingPlans(true);
      setError(null);
      setAvailablePlans([]);
      setSelectedPlanIds(new Set());

      fetchAvailableInsurancePlans(selectedState)
        .then((plans) => {
          setAvailablePlans(plans);
        })
        .catch((err) => {
          setError('Failed to load insurance plans');
          console.error(err);
        })
        .finally(() => {
          setIsLoadingPlans(false);
        });
    } else {
      setAvailablePlans([]);
      setSelectedPlanIds(new Set());
    }
  }, [selectedState, isOpen]);

  // Filter plans by search query and exclude already assigned
  const filteredPlans = useMemo(() => {
    let filtered = availablePlans.filter(
      (plan) => !existingInsuranceIds.has(plan.id)
    );

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((plan) => {
        const friendlyName = plan.friendlyName?.toLowerCase() || '';
        const companyName = plan.companyName?.toLowerCase() || '';
        const emrPlan = plan.emrInsurancePlan?.toLowerCase() || '';
        return (
          friendlyName.includes(query) ||
          companyName.includes(query) ||
          emrPlan.includes(query)
        );
      });
    }

    return filtered.sort((a, b) => {
      const nameA = a.friendlyName || a.companyName || '';
      const nameB = b.friendlyName || b.companyName || '';
      return nameA.localeCompare(nameB);
    });
  }, [availablePlans, existingInsuranceIds, searchQuery]);

  const handleSelectAll = () => {
    if (selectedPlanIds.size === filteredPlans.length) {
      setSelectedPlanIds(new Set());
    } else {
      setSelectedPlanIds(new Set(filteredPlans.map((plan) => plan.id)));
    }
  };

  const handleTogglePlan = (planId: string) => {
    const newSelected = new Set(selectedPlanIds);
    if (newSelected.has(planId)) {
      newSelected.delete(planId);
    } else {
      newSelected.add(planId);
    }
    setSelectedPlanIds(newSelected);
  };

  const handleAdd = async () => {
    if (!selectedState) {
      setError('Please select a state');
      return;
    }

    if (selectedPlanIds.size === 0) {
      setError('Please select at least one insurance plan');
      return;
    }

    if (practiceIds.length === 0) {
      setError('No practices available for this provider');
      return;
    }

    setIsAdding(true);
    setError(null);

    try {
      const ageRangeCoerced = coerceRangeForBackend(defaultAgeRange);
      const gridEntries = [];

      // Map combined status to credentialStatus and value
      const { credentialStatus, value } = getCombinedStatusMapping(defaultCombinedStatus);

      // Create entries for each selected plan × each practice
      for (const planId of selectedPlanIds) {
        for (const practiceId of practiceIds) {
          gridEntries.push({
            practiceId,
            providerId,
            plan_id: planId,
            credentialStatus,
            value,
            minimumAge: ageRangeCoerced.minimumAge,
            maximumAge: ageRangeCoerced.maximumAge,
          });
        }
      }

      if (gridEntries.length === 0) {
        setError('No entries to add');
        setIsAdding(false);
        return;
      }

      await createGrid({ gridEntries });

      toast({
        title: 'Insurance plans added successfully',
        description: `Added ${selectedPlanIds.size} insurance plan(s) to ${practiceIds.length} practice(s)`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to add insurance plans';
      setError(errorMessage);
      toast({
        title: 'Error adding insurance plans',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setAvailablePlans([]);
    setSelectedPlanIds(new Set());
    setSearchQuery('');
    setDefaultCombinedStatus('No Status');
    setDefaultAgeRange({ min: null, max: null });
    onClose();
  };

  const totalEntriesToCreate = selectedPlanIds.size * practiceIds.length;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="5xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" zIndex={1400} />
      <ModalContent maxW="90vw" maxH="90vh" zIndex={1500}>
        <ModalHeader>
          <Text fontSize="1.25rem" fontWeight="700" color="text.secondary">
            Add New Insurance Plans
          </Text>
          <Text fontSize="0.875rem" color="gray.600" fontWeight="400" mt={2}>
            Select insurance plans from a state and add them to this provider
            for all practices
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto" maxH="calc(90vh - 200px)">
          <VStack spacing={6} align="stretch">
            {/* State Selection */}
            <FormControl isRequired>
              <FormLabel>State</FormLabel>
              <Select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                placeholder="Select a state"
              >
                {availableStates.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Default Values Section */}
            {selectedState && (
              <Box
                p={4}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                bg="gray.50"
              >
                <Text fontSize="1rem" fontWeight="600" mb={4}>
                  Default Values
                </Text>
                <Text fontSize="0.875rem" color="gray.600" mb={4}>
                  These values will be applied to all selected insurance plans
                  for all practices
                </Text>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Status</FormLabel>
                    <Menu>
                      <MenuButton
                        as={Button}
                        size="sm"
                        minW="200px"
                        maxW="300px"
                        width="fit-content"
                        height="2.75rem"
                        bg={getStatusConfigForOption(defaultCombinedStatus).bgColor}
                        color="white"
                        borderRadius="0.3125rem"
                        fontWeight="600"
                        fontSize="0.75rem"
                        _hover={{
                          filter: 'brightness(1.05)',
                        }}
                        _active={{
                          filter: 'brightness(0.95)',
                        }}
                        rightIcon={
                          <Box
                            as="svg"
                            width="12px"
                            height="12px"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </Box>
                        }
                        textAlign="left"
                      >
                        <Text
                          noOfLines={1}
                          textAlign="left"
                        >
                          {getStatusConfigForOption(defaultCombinedStatus).label}
                        </Text>
                      </MenuButton>
                      <MenuList
                        minW="250px"
                        maxW="300px"
                        p={2}
                        borderRadius="0.5rem"
                        boxShadow="lg"
                        border="1px solid"
                        borderColor="gray.200"
                      >
                        {COMBINED_STATUS_OPTIONS.map((option) => {
                          const optionConfig = getStatusConfigForOption(option);
                          const isSelected = option === defaultCombinedStatus;

                          return (
                            <MenuItem
                              key={option}
                              onClick={() => setDefaultCombinedStatus(option)}
                              bg={
                                isSelected
                                  ? 'blue.50'
                                  : 'transparent'
                              }
                              _hover={{
                                bg: isSelected
                                  ? 'blue.100'
                                  : 'gray.50',
                              }}
                              borderRadius="0.375rem"
                              mb={1}
                              p={2}
                              minH="auto"
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                gap="0.75rem"
                                width="100%"
                              >
                                <Box
                                  width="1rem"
                                  height="1rem"
                                  borderRadius="0.25rem"
                                  bg={optionConfig.bgColor}
                                  flexShrink={0}
                                />
                                <Box flex="1">
                                  <Text
                                    fontSize="0.875rem"
                                    fontWeight={
                                      isSelected ? '600' : '500'
                                    }
                                    color="gray.800"
                                    mb={0.5}
                                  >
                                    {optionConfig.label}
                                  </Text>
                                  <Text
                                    fontSize="0.75rem"
                                    color="gray.600"
                                    lineHeight="1.2"
                                  >
                                    {
                                      optionConfig.helperText.split(
                                        ' | '
                                      )[0]
                                    }
                                  </Text>
                                </Box>
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </Menu>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Age Range</FormLabel>
                    <Box maxW="300px">
                      <AgeRangeSelect
                        value={defaultAgeRange}
                        onChange={(range) => setDefaultAgeRange(range)}
                        placeholder="Select age range"
                      />
                    </Box>
                  </FormControl>
                </VStack>
              </Box>
            )}

            {/* Error Display */}
            {error && (
              <Alert status="error">
                <AlertIcon />
                {error}
              </Alert>
            )}

            {/* Plans List */}
            {selectedState && (
              <>
                {/* Search */}
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search insurance plans..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>

                {/* Loading State */}
                {isLoadingPlans && (
                  <Flex justify="center" py={8}>
                    <Spinner size="lg" />
                  </Flex>
                )}

                {/* Plans Table */}
                {!isLoadingPlans && filteredPlans.length > 0 && (
                  <Box>
                    <Flex justify="space-between" align="center" mb={4}>
                      <Text fontSize="1rem" fontWeight="600">
                        Select Plans ({selectedPlanIds.size} of{' '}
                        {filteredPlans.length} selected)
                      </Text>
                      <Button size="sm" onClick={handleSelectAll}>
                        {selectedPlanIds.size === filteredPlans.length
                          ? 'Deselect All'
                          : 'Select All'}
                      </Button>
                    </Flex>
                    <Box
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      maxH="400px"
                      overflowY="auto"
                      overflowX="hidden"
                      position="relative"
                      zIndex={1}
                    >
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th width="50px">
                              <Checkbox
                                isChecked={
                                  selectedPlanIds.size === filteredPlans.length &&
                                  filteredPlans.length > 0
                                }
                                isIndeterminate={
                                  selectedPlanIds.size > 0 &&
                                  selectedPlanIds.size < filteredPlans.length
                                }
                                onChange={handleSelectAll}
                              />
                            </Th>
                            <Th>Plan Name</Th>
                            <Th>Company Name</Th>
                            <Th>EMR Plan</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredPlans.map((plan) => {
                            const isSelected = selectedPlanIds.has(plan.id);
                            return (
                              <Tr key={plan.id}>
                                <Td>
                                  <Checkbox
                                    isChecked={isSelected}
                                    onChange={() => handleTogglePlan(plan.id)}
                                  />
                                </Td>
                                <Td>{plan.friendlyName || '-'}</Td>
                                <Td>{plan.companyName || '-'}</Td>
                                <Td>{plan.emrInsurancePlan || '-'}</Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )}

                {/* No Plans Found */}
                {!isLoadingPlans &&
                  selectedState &&
                  filteredPlans.length === 0 && (
                    <Alert status="info">
                      <AlertIcon />
                      {searchQuery.trim()
                        ? 'No insurance plans found matching your search'
                        : availablePlans.length === 0
                        ? 'No insurance plans found for this state'
                        : 'All insurance plans for this state are already assigned to this provider'}
                    </Alert>
                  )}

                {/* Preview */}
                {selectedPlanIds.size > 0 && (
                  <Alert status="info">
                    <AlertIcon />
                    <Text>
                      Will create {totalEntriesToCreate} insurance plan
                      {totalEntriesToCreate !== 1 ? 's' : ''} (
                      {selectedPlanIds.size} plan
                      {selectedPlanIds.size !== 1 ? 's' : ''} ×{' '}
                      {practiceIds.length} practice
                      {practiceIds.length !== 1 ? 's' : ''})
                    </Text>
                  </Alert>
                )}
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleAdd}
            isLoading={isAdding}
            isDisabled={
              !selectedState ||
              selectedPlanIds.size === 0 ||
              practiceIds.length === 0 ||
              isAdding
            }
          >
            Add Plans
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddInsurancePlansModal;
